import {applySnapshot, flow, getSnapshot, Instance, types} from "mobx-state-tree";
import {parsePhoneNumberFromString as parseMobile} from 'libphonenumber-js/mobile'
import Cookies from 'js-cookie';

import agent, {isLocalHost} from "../agent";
import {validateEmail, validatePhone} from "../utils/Validations";
import {Group} from "./models";
import * as Analytics from "../utils/Analytics"
import * as StorageService from "../services/StorageService"

const AuthStore = types
    .model({
        verificationToken: types.string,
        backoffTime: types.number,
        phoneNumber: types.string,
        countryCode: types.string,
        email: types.string,
        jwt: types.string,
        loggedIn: types.boolean,
        isInMiddleOfLoggingIn: types.boolean,
        returnToGetStartedScreen: types.boolean,
        defaultCountryCode: types.maybe(types.model({
            flag: types.string,
            name: types.string,
            alpha2Code: types.string,
            label: types.string,
            value: types.string,
        })),
        name: types.string,
        avatar: types.string,
        groups: types.array(Group),
        isUnknownPhoneNumber: types.boolean
    })
    .views(self => ({
        get emailCanProceed(): boolean {
            return validateEmail(self.email)
        },
        get fullPhoneNumber() { // in-use
            const phoneNumber = parseMobile(`${self.countryCode}${self.phoneNumber}`);
            return phoneNumber ? phoneNumber.number : ''
        },
        get phoneNumberCanProceed(): boolean {
            return validatePhone(self.countryCode, self.phoneNumber)
        }
    }))
    .actions(self => {
            let initialState = {};
            return {
                afterCreate() {
                    initialState = getSnapshot(self);
                },
                reset: () => {
                    Cookies.remove('refresh_token'); // logout
                    StorageService.clear();
                    applySnapshot(self, initialState);
                },
                setEmail(value: string) {
                    self.email = value;
                },
                setPhoneNumber(value: string) {
                    self.phoneNumber = value;
                },
                setCountryCode(value: string) {
                    self.countryCode = value;
                },
                setReturnToGetStartedScreen(value: boolean) {
                    self.returnToGetStartedScreen = value;
                },
                setDataAfterLogin(data: any) {
                    const userId = data.id;
                    Analytics.setUserId(userId);
                    self.jwt = data.jwtoken;
                    self.email = data.email ? data.email : "";
                    self.loggedIn = true;
                    self.name = data.name;
                    self.avatar = data.avatar;
                    self.groups = data.groups.map(({data}: any) => {
                        const {group_avatar, group_context_id, group_name, group_select_id, group_owner, group_allow_send_post} = data;
                        return {
                            id: group_context_id,
                            name: group_name,
                            avatar: group_avatar,
                            selectId: group_select_id,
                            isOwner: !group_owner || userId === group_owner,
                            allowSendPost: group_allow_send_post,
                            allowAddUser: group_allow_send_post
                        };
                    });

                    agent.setToken(data.jwtoken);
                },
                requestPhoneNumberVerificationCode: flow(function* () {
                    self.isUnknownPhoneNumber = false;
                    try {
                        yield agent.Auth.checkPhoneNumber(self.countryCode, self.phoneNumber);
                    } catch (e) {
                        self.isUnknownPhoneNumber = true;
                        return false;
                    }
                    const data = yield agent.Auth.requestVerificationCode(self.countryCode, self.phoneNumber);
                    self.verificationToken = data.token ? data.token : '';
                    self.backoffTime = data.backoff ? data.backoff : 0;
                    return true;
                }),
                loginWithRefreshToken: flow(function* (): Generator<any, boolean, any> {
                    try {
                        self.isInMiddleOfLoggingIn = true;

                        const tokenInfo = StorageService.getRefreshToken()
                        if (!tokenInfo) return false;

                        let data: any;
                        try {
                            data = yield agent.Auth.getJwt(tokenInfo.refreshToken);
                        } catch (ignore) {
                        }

                        if (!data || !data.username || !data.jwtoken) {
                            // account does not exist
                            return false
                        }

                        // @ts-ignore
                        self.setDataAfterLogin(data);

                        return true

                    } finally {
                        self.isInMiddleOfLoggingIn = false;
                    }
                }),
                verifyPhoneNumberAndVerificationCode: flow(function* (code: string): Generator<any, boolean, any> {

                    if (!self.countryCode || !self.phoneNumber) {
                        throw new Error('verifyPhoneNumberAndVerificationCode error: countryCode or phoneNumber dont exists')
                    }

                    const data = yield agent.Auth.verifyPhoneNumberAndVerificationCode(self.countryCode, self.phoneNumber, code);

                    if (!data || !data.username || !data.jwtoken) {
                        // account does not exist
                        return false
                    }

                    StorageService.saveRefreshToken(data.id, data.refresh_token)
                    // Cookies.set("refresh_token", data.refresh_token, {secure: !isLocalHost(), expires: 365});

                    // @ts-ignore
                    self.setDataAfterLogin(data);

                    return true;
                })
            }
        }
    );


export interface IAuthStore extends Instance<typeof AuthStore> {
}

export const AuthDefaultStore = {
    verificationToken: '',
    backoffTime: 0,
    phoneNumber: '',
    countryCode: '',
    jwt: '',
    loggedIn: false,
    isInMiddleOfLoggingIn: false,
    returnToGetStartedScreen: false,
    defaultCountryCode: undefined,
    isUnknownPhoneNumber: false,
    email: '',
    name: '',
    avatar: '',
    groups: []
};

export default AuthStore;
